import { DeleteOfferConfirmation } from "./modals/delete-offer-confirmation";
import { OfferCardActionButtons } from "./offer-card-action-buttons";
import { OfferCardDetails } from "./offer-card-details";
import { OfferCardLocations } from "./offer-card-locations";
import { OfferCardRepViewTable } from "./offer-card-rep-view-table";
import { OfferCardTopBar } from "./offer-card-top-bar";
import { OfferStatusUpdate } from "./modals/offer-status-update";
import { RateEntry } from "./modals/rate-entry";
import { RenewOfferConfirmation } from "./modals/renew-offer";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import SlideToggle from "react-slide-toggle";
import eases from "eases";
import { getLocationFormat } from "../../../../../old/utils/parseData";
import { isEqual } from "lodash";
import { useDatesLocation } from "../hooks";
import { useGetUserInfo } from "../../../../../shared/infrastructure/services/user.services";
import { useHasPermissions } from "../../../../../public/auth/infrastructure/store/auth-context";
import { useTranslation } from "react-i18next";
import { buildHighwayUrl } from "../utils/helpers";
import {
  CardFlip,
  CircleIcon,
  HandIcon,
  LinkIcon,
  Modal,
  PencilIcon,
  RepeatIcon,
  ShoppingCartIcon,
  Tooltip,
  TrashIcon,
  TruckIcon,
  useDisclosure,
} from "../../../../../shared/infrastructure";
import {
  OfferCardMarginTooltip,
  OfferCardMarginTooltipContainer,
} from "./offer-card-margin-tooltip";
import { OfferCardSectionT, OfferT, OfferViewT } from "../../../domain";
import React, { LegacyRef, useEffect, useState } from "react";

type OfferCardLayoutProps = {
  section1: React.ReactNode;
  section2: React.ReactNode;
  section3: React.ReactNode;
  section4: React.ReactNode;
  details: React.ReactNode;
  table?: React.ReactNode;
  showDetails: boolean;
  showEditDeleteOptions?: boolean;
  showRenewOption?: boolean;
  data: OfferT;
  toggleView?: () => void;
  showHighlighted?: boolean;
  isManaged?: boolean;
  view?: OfferViewT;
  isRemoved?: boolean;
  tableViewIsOpen?: boolean;
  isDedicated: boolean;
  toggleTable?: () => void;
  cardType?: OfferCardSectionT;
};

const OfferCardLayout = ({
  section1,
  section2,
  section3,
  section4,
  table,
  data,
  view = "offer",
  showEditDeleteOptions = false,
  showRenewOption = false,
  showHighlighted = false,
  isManaged = false,
  isRemoved = false,
}: OfferCardLayoutProps): React.ReactElement => {
  const {
    isOpen: isUpdateOpen,
    close: closeUpdate,
    open: openUpdate,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    close: closeDelete,
    open: openDelete,
  } = useDisclosure();
  const {
    isOpen: isRenewOpen,
    close: closeRenew,
    open: openRenew,
  } = useDisclosure();
  const {
    isOpen: isSetStatusApproved,
    close: closeSetStatusApproved,
    open: openSetStatusApproved,
  } = useDisclosure();
  const {
    isOpen: isSetStatusCounter,
    close: closeSetStatusCounter,
    open: openSetStatusCounter,
  } = useDisclosure();
  const {
    isOpen: isSetStatusRejected,
    close: closeSetStatusRejected,
    open: openSetStatusRejected,
  } = useDisclosure();

  const hasPermissions = useHasPermissions("update:status-approvals-queue");

  return (
    <section
      className={[
        "group relative mx-auto max-w-[335px] rounded-[5px] md:max-w-[648px] xl:max-w-none",
        showHighlighted ? "border-[2px] border-solid border-[#2196F3]" : "",
        isManaged || isRemoved ? "bg-transparent" : "bg-white",
      ].join(" ")}
    >
      <section
        className={[
          /*
           * "relative grid grid-cols-2 gap-1",  // <-- simpler
           * "lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-2 lg:p-4",                // <-- force 5 cols on lg
           */
          isManaged || isRemoved
            ? "rounded-[5px] border-[2px] border-solid border-[#E3E3E3] opacity-[.5]"
            : "",
        ].join(" ")}
      >
        {view === "offer" ? (
          <div
            className="
            /* default: 
            mobile        */ /* tablet */
            /*     large desktop */
            relative     grid grid-cols-1 items-stretch gap-1
            px-4 py-4
            md:grid-cols-3
          2xl:grid-cols-5
          "
          >
            {/* section1 always first */}
            <div className="order-1">{section1}</div>

            {/* section2: mobile last, tablet last, large spans 2 cols */}
            <div className="order-4 col-span-full md:order-4 2xl:order-2  2xl:col-span-2 2xl:border-r 2xl:border-dashed 2xl:border-gray-300">
              {section2}
            </div>

            {/* section3: mobile second, tablet second, large after section2 */}
            <div className="order-2 border-b border-dashed border-gray-300 md:order-2 md:border-r md:border-b-0 2xl:order-3">
              {section3}
            </div>

            {/* section4: mobile third, tablet third, large last */}
            <div className="border-gray order-3 border-b border-dashed md:order-3 md:border-b-0 2xl:order-4">
              {section4}
            </div>
          </div>
        ) : (
          <div
            className="
            /* default: 
            mobile        */ /* tablet */
            /*     large desktop */
            relative     grid grid-cols-1 items-stretch gap-1
            px-4 py-4
            md:grid-cols-2
          2xl:grid-cols-5
          "
          >
            {/* section1 always first */}
            <div className="order-1">{section1}</div>

            {/* section2: mobile last, tablet last, large spans 2 cols */}
            <div className="order-3 col-span-full md:order-3 2xl:order-2  2xl:col-span-3 2xl:border-r 2xl:border-dashed 2xl:border-gray-300">
              {section2}
            </div>

            {/* section4: mobile third, tablet third, large last */}
            <div className="border-gray order-2 border-b border-dashed md:order-2 md:border-b-0 2xl:order-4">
              {section4}
            </div>
          </div>
        )}

        {/*  */}
        {showEditDeleteOptions && !isManaged && !isRemoved && (
          <section className="absolute top-[15px] right-0 z-[-1000] grid h-[calc(100%-76px)] grid-flow-col transition-all duration-[150] group-hover:right-[-32px]">
            <section className="h-full w-[6px] bg-transparent" />
            <section className="grid w-[26px] content-center gap-[10px] rounded-[5px] bg-[#FFFFFF] p-[5px]">
              <Tooltip text="Edit">
                <button onClick={openUpdate}>
                  <PencilIcon
                    width={16}
                    height={16}
                    className="cursor-pointer transition-all duration-[300] hover:text-[#3D3BEF]"
                  />
                </button>
              </Tooltip>
              <Tooltip text="Delete">
                <button onClick={openDelete}>
                  <TrashIcon
                    width={16}
                    height={16}
                    className="cursor-pointer transition-all duration-[300] hover:text-[#3D3BEF]"
                  />
                </button>
              </Tooltip>
              {hasPermissions ? (
                <Tooltip text="Approved">
                  <button onClick={openSetStatusApproved}>
                    <CircleIcon
                      width={16}
                      height={16}
                      className="cursor-pointer transition-all duration-[300] hover:text-[#3D3BEF]"
                      fill="#28A745"
                    />
                  </button>
                </Tooltip>
              ) : null}
              {hasPermissions ? (
                <Tooltip text="Counter">
                  <button onClick={openSetStatusCounter}>
                    <CircleIcon
                      width={16}
                      height={16}
                      className="cursor-pointer transition-all duration-[300] hover:text-[#3D3BEF]"
                      fill="#FFC107"
                    />
                  </button>
                </Tooltip>
              ) : null}
              {hasPermissions ? (
                <Tooltip text="Rejected">
                  <button onClick={openSetStatusRejected}>
                    <CircleIcon
                      width={16}
                      height={16}
                      className="cursor-pointer transition-all duration-[300] hover:text-[#3D3BEF]"
                      fill="#DC3545"
                    />
                  </button>
                </Tooltip>
              ) : null}
            </section>
          </section>
        )}

        <Modal
          open={isSetStatusApproved}
          onClose={closeSetStatusApproved}
          content={
            <OfferStatusUpdate
              initialStatus="approved"
              data={data}
              close={close}
            />
          }
          classNames={{
            content: "overflow-hidden p-[20px] min-w-[332px] min-h-[214px]",
            paper: "rounded-[5px]",
          }}
        />

        <Modal
          open={isSetStatusCounter}
          onClose={closeSetStatusCounter}
          content={
            <OfferStatusUpdate
              initialStatus="counter"
              data={data}
              close={close}
            />
          }
          classNames={{
            content: "overflow-hidden p-[20px] min-w-[332px] min-h-[214px]",
            paper: "rounded-[5px]",
          }}
        />

        <Modal
          open={isSetStatusRejected}
          onClose={closeSetStatusRejected}
          content={
            <OfferStatusUpdate
              initialStatus="rejected"
              data={data}
              close={close}
            />
          }
          classNames={{
            content: "overflow-hidden p-[20px] min-w-[332px] min-h-[214px]",
            paper: "rounded-[5px]",
          }}
        />

        {showRenewOption && (
          <section className="absolute top-[38px] right-0 z-[-1000] grid h-[calc(100%-76px)] grid-flow-col transition-all duration-[150] group-hover:right-[-32px]">
            <section className="h-full w-[6px] bg-transparent" />
            <section className="grid w-[26px] content-center gap-[40px] rounded-[5px] bg-[#FFFFFF] p-[5px]">
              <Tooltip text="Renew">
                <button onClick={openRenew}>
                  <RepeatIcon
                    width={16}
                    height={16}
                    className="cursor-pointer transition-all duration-[300] hover:text-[#3D3BEF]"
                  />
                </button>
              </Tooltip>
            </section>
          </section>
        )}
      </section>

      {table ?? null}
      {showEditDeleteOptions && !isManaged && (
        <React.Fragment>
          <Modal
            open={isUpdateOpen}
            onClose={closeUpdate}
            content={<RateEntry onClose={closeUpdate} offer={data} />}
            classNames={{
              content: "overflow-y-auto overflow-x-hidden p-0",
              paper: "rounded-[5px] min-w-[362px]",
            }}
          />
          <Modal
            open={isDeleteOpen}
            onClose={closeDelete}
            content={
              <DeleteOfferConfirmation close={closeDelete} data={data} />
            }
            classNames={{
              content: "overflow-hidden p-[20px] w-[350px]",
              paper: "rounded-[5px]",
            }}
          />
        </React.Fragment>
      )}
      {showRenewOption && (
        <React.Fragment>
          <Modal
            open={isRenewOpen}
            onClose={closeRenew}
            content={<RenewOfferConfirmation close={closeRenew} data={data} />}
            classNames={{
              content: "overflow-hidden p-[20px] w-[350px]",
              paper: "rounded-[5px]",
            }}
          />
        </React.Fragment>
      )}
      {isManaged && (
        <section className="absolute top-0 left-0 grid h-full w-full place-content-center">
          <section className="grid grid-flow-col items-center gap-[5px] rounded-[5px] border-[2px] border-solid border-[#9E9E9E] bg-[#FFFFFF] p-[14px] text-[16px] font-[700] text-[#0C1F3D]">
            <HandIcon width={24} height={24} />
            <span>This offer has already been managed</span>
          </section>
        </section>
      )}
      {isRemoved && (
        <section className="absolute top-0 left-0 grid h-full w-full place-content-center">
          <section className="grid grid-flow-col items-center gap-[5px] rounded-[5px] border-[2px] border-solid border-[#9E9E9E] bg-[#FFFFFF] p-[14px] text-[16px] font-[700] text-[#0C1F3D]">
            <TrashIcon width={24} height={24} />
            <span>This offer was deleted</span>
          </section>
        </section>
      )}
    </section>
  );
};

type OfferCardProps = {
  data: OfferT;
  cardType: OfferCardSectionT;
  showHighlighted?: boolean;
  isManaged?: boolean;
  isRemoved?: boolean;
};

const CarrierSection = ({ data }: { data: OfferT }): React.ReactElement => {
  const [t] = useTranslation("global");

  return (
    <div className="h-full rounded-lg border bg-black p-4 text-white">
      <h2 className="text-lg font-bold">{data.order_number}</h2>
      <p className="py-3 font-bold">{data.customer?.name}</p>
      <section className="grid grid-cols-[12px_1fr_auto] items-center gap-[4px]">
        <span />
        <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-[300] ">
          Sales
        </span>
        <span className="ml-auto text-[12px] font-[400] ">
          {data.customer?.salesperson_id
            ? data.customer?.salesperson_id
            : "- -"}
        </span>
      </section>
      <section className="grid grid-cols-[12px_1fr_auto] items-center gap-[4px]">
        <span />
        <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-[300] ">
          AM
        </span>
        <span className="ml-auto text-[12px] font-[400] ">
          {data.customer?.ops_user_1 ? data.customer?.ops_user_1 : "- -"}
        </span>
      </section>
      <section className="grid grid-cols-[12px_1fr_auto] items-center gap-[4px]">
        <span />
        <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-[300] ">
          Ops
        </span>
        <span className="ml-auto text-[12px] font-[400] ">
          {data.customer?.ops_user_2 ? data.customer?.ops_user_2 : "- -"}
        </span>
      </section>
      <section className="grid grid-cols-[12px_1fr_auto] items-center gap-[4px]">
        <span />
        <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-[300] ">
          Built
        </span>
        <span className="ml-auto text-[12px] font-[400] ">
          {useDatesLocation(data.ordered_date, "")[0]}
        </span>
      </section>
      <section className="grid grid-cols-[12px_1fr_auto] items-center gap-[4px]">
        <span />
        <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-semibold">
          {t<string>("context.private.components.offer-card-score.order-value")}
        </span>
        <span className="ml-auto text-[12px] font-semibold">
          {data.order_value
            ? `$${data.order_value.toLocaleString("en-US")} USD`
            : "- -"}
        </span>
      </section>
    </div>
  );
};

const CustomerSection = ({ data }: { data: OfferT }): React.ReactElement => {
  return (
    <div className="flex h-full flex-col justify-between rounded-lg bg-white p-4 text-sm font-normal">
      <section className="grid grid-cols-[1fr_12px_auto] items-center gap-[4px]">
        <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-semibold ">
          Carrier
        </span>
        <LinkIcon width={20} height={20} fill="#3C7CF3" />
        <span className="ml-auto text-[12px] font-[400] ">
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={buildHighwayUrl(data.carrier?.highway_id || "")}
            className="grid cursor-pointer content-start gap-1"
          >
            <span
              title={data.carrier.name}
              className="overflow-hidden text-ellipsis whitespace-nowrap px-[5px] text-sm font-[700] capitalize text-[#3C7CF3]"
            >
              {data.carrier.name.toLowerCase()}
            </span>
          </a>
        </span>
      </section>
      <section className="flex items-center gap-[4px]">
        <span className="overflow-hidden text-ellipsis whitespace-pre-wrap pr-2 text-sm font-[300] text-[#484848]">
          Rep
        </span>
        <span
          className="ml-auto  truncate text-[12px] font-[400] text-[#484848]"
          title={data.posted_by_user?.name}
        >
          {data.posted_by_user?.name ? data.posted_by_user?.name : "- -"}
        </span>
      </section>
      <section className="flex items-center gap-[4px]">
        <span className="pr-2 text-sm font-[300] text-[#484848]">Source</span>
        <span
          className="ml-auto  truncate text-[12px] font-[700] text-[#484848]"
          title={data.offer_source}
        >
          {data.offer_source ? data.offer_source : "- -"}
        </span>
      </section>
      <section className="flex items-center gap-[4px]">
        <span className="pr-2 text-sm font-[300] text-[#484848]">MT</span>
        <span className="ml-auto  truncate text-[12px] font-[700] text-[#484848]">
          {getLocationFormat(data.truck_location)} / {data.empty_miles || "--"}{" "}
          Mi
        </span>
      </section>
      {data.is_empty_truck && (
        <section className="grid grid-cols-[1fr_auto] items-center justify-between gap-[10px]">
          <section className="grid grid-cols-[12px_1fr] items-center gap-1 text-[#6296F8]">
            <TruckIcon width={12} height={12} />
            <span className="text-sm font-[700]">Empty Truck</span>
          </section>
        </section>
      )}
      <section className="items-center">
        <span className="ml-[2px] overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-[300] text-[#484848]">
          {data.comments || data.status_comment || "- -"}
        </span>
      </section>
    </div>
  );
};

const RatesSection = ({
  data,
  setView,
  view,
}: {
  data: OfferT;
  setView: (view?: string) => void;
  view: OfferViewT;
}): React.ReactElement => {
  const [t] = useTranslation("global");

  const margin =
    data.total_charge && data.rate ? data.total_charge - data.rate : null;

  return (
    <div className="flex h-full flex-col justify-between rounded-lg rounded-lg  bg-white px-4 pt-4">
      <section className="grid grid-cols-[12px_1fr_auto] items-center gap-[4px]">
        <ShoppingCartIcon width={12} height={12} fill="black" />
        <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-[700] text-[#484848]">
          {t<string>(
            "context.private.components.offer-card-fare.customer-rate"
          )}
        </span>
        <span className="ml-auto text-sm font-normal text-[#4CAF50]">
          {data.total_charge
            ? `$${data.total_charge.toLocaleString("en-US")} USD`
            : "- -"}
        </span>
      </section>
      <section className="grid grid-cols-[12px_1fr_auto] items-center gap-[4px]">
        <span />
        <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-[300] text-[#484848]">
          {t<string>("context.private.components.offer-card-fare.max-buy")}
        </span>
        <span className="ml-auto text-[12px] font-[400] text-[#484848]">
          {data.max_buy
            ? `$${data.max_buy.toLocaleString("en-US")} USD`
            : "- -"}
        </span>
      </section>
      <section className="grid grid-cols-[12px_1fr_auto] items-center gap-[4px]">
        <span />
        <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-[300] text-[#484848]">
          {t<string>("context.private.components.offer-card-score.lookup-rate")}
        </span>
        <span className="ml-auto text-[12px] font-[400] text-[#484848]">
          {data.lookup_rate
            ? `$${data.lookup_rate.toLocaleString("en-US")} USD`
            : "- -"}
        </span>
      </section>
      <OfferCardMarginTooltipContainer>
        <section className="grid grid-cols-[12px_1fr_auto] items-center gap-[4px]">
          <span />
          <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-[700] text-[#484848]">
            {t<string>(
              "context.private.components.offer-card-score.offer-rate"
            )}
          </span>
          <span className="ml-auto text-[12px] font-[400] font-semibold text-blue-600">
            {data.rate ? `$${data.rate.toLocaleString("en-US")} USD` : "- -"}
          </span>
        </section>
        <OfferCardMarginTooltip offer={data} />
      </OfferCardMarginTooltipContainer>
      <section
        className={[
          "grid grid-cols-[12px_1fr_auto] items-center gap-[4px]",
          margin === null
            ? "text-[#484848]"
            : margin >= 0
            ? "text-green-600"
            : "text-red-500",
        ].join(" ")}
      >
        <span />
        <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-[300] ">
          {t<string>("context.private.components.offer-card-score.margin")}
        </span>
        <span className="ml-auto text-[12px] font-[400] ">
          {margin !== null
            ? margin < 0
              ? `-$${Math.abs(margin).toLocaleString("en-US")}`
              : `$${margin.toLocaleString("en-US")}`
            : "- -"}
        </span>
      </section>
      <div className="relative flex w-full flex-col py-1 lg:inline-flex">
        <div className="flex justify-end ">
          <OfferCardActionButtons
            offer={data}
            toggleView={() => setView("rep")}
            view={view}
            tableViewIsOpen={false}
          />
        </div>
      </div>
    </div>
  );
};

const tenderTypeIdColors: Record<string, string> = {
  AMAZON: "#FF0000",
  BOUNCE: "#FFFF33",
  FINE: "#FF0000",
  FIRSTONE: "#993300",
  FLEX: "#0066FF",
  HIGHVALU: "#FFCC00",
  HOT: "#FF9900",
  LIVEBOUN: "#009933",
  MORNING: "#0099FF",
  ROLLED: "#663399",
};

const findTenderTypeColor = (tenderTypeId = ""): string => {
  return tenderTypeIdColors[tenderTypeId?.toUpperCase()]
    ? tenderTypeIdColors[tenderTypeId?.toUpperCase()]
    : "bg-[#D1D5DB]";
};

const orderTypeIdColors: Record<string, string> = {
  EDGELOCK: "#00FF00",
  FLEXIBLE: "#00FFFF",
  FLEXPORT: "#0000FF",
  HOT: "#FFCC96",
  NBOUND: "#FF0000",
  // INTERNAL: "#000000",
};

const findOrderTypeColor = (orderTypeId = ""): string => {
  return orderTypeIdColors[orderTypeId?.toUpperCase()]
    ? orderTypeIdColors[orderTypeId?.toUpperCase()]
    : "bg-[#D1D5DB]";
};

const LocationsSection = ({
  data,
  cardType,
}: {
  data: OfferT;
  cardType: OfferCardSectionT;
}): React.ReactElement => {
  return (
    <div className="grid  grid-rows-5 gap-2 rounded-lg bg-white px-4">
      {/* Row 1: Origin / Distance / Destination */}
      <OfferCardTopBar data={data} cardType={cardType} />

      <div className="row-span-2">
        <OfferCardLocations offer={data} showLogHistory />
      </div>
      {/* Origin */}

      {/* Row 2: RevCode / Equip / HOT */}
      <div className="grid grid-cols-3 items-center justify-between gap-4">
        <section className="flex justify-center  gap-[4px]">
          <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-[300] text-[#484848]">
            Rev Code:
          </span>
          <span className="ml-4 text-[12px] font-[400] text-[#484848]">
            {data.revenue_code_id ? data.revenue_code_id : "- -"}
          </span>
        </section>
        <section className="flex justify-center gap-[4px]">
          <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-[300] text-[#484848]">
            Equip:
          </span>
          <span className="mx-4 text-[12px] font-[400] text-[#484848]">
            {data.equipment_type?.description
              ? data.equipment_type?.description
              : "- -"}
          </span>
        </section>
        <section className="flex justify-center gap-[4px]">
          <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-[300] text-[#484848]">
            Weight:
          </span>
          <span className="ml-4 text-[12px] font-[400] text-[#484848]">
            {data.weight ? data.weight : "- -"} lb
          </span>
        </section>
      </div>

      <div className="grid grid-cols-2 items-center justify-between gap-4">
        <div className="flex items-center justify-center">
          <span
            className={["rounded px-2 py-1 text-xs font-bold text-black"].join(
              " "
            )}
            style={{
              backgroundColor: findTenderTypeColor(data.tender_type_id),
            }}
          >
            {data.tender_type_id ? data.tender_type_id : "- -"}
          </span>
        </div>
        <div className="flex items-center justify-center">
          <span
            className={["rounded px-2 py-1 text-xs font-bold text-black"].join(
              " "
            )}
            style={{
              backgroundColor: findOrderTypeColor(data.order_type_id || ""),
            }}
          >
            {data.order_type_id ? data.order_type_id : "- -"}
          </span>
        </div>
      </div>
    </div>
  );
};

const LocationsRepViewSection = ({
  data,
}: {
  data: OfferT;
}): React.ReactElement => {
  return (
    <div className="grid  h-full grid-rows-4 gap-2 rounded-lg bg-white px-4 pt-4">
      <div className="row-span-2">
        <OfferCardLocations offer={data} showLogHistory />
      </div>

      <div className="grid grid-cols-3 items-center justify-between gap-4">
        <section className="flex justify-center  gap-[4px]">
          <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-[300] text-[#484848]">
            Rev Code:
          </span>
          <span className="ml-4 text-[12px] font-[400] text-[#484848]">
            {data.revenue_code_id ? data.revenue_code_id : "- -"}
          </span>
        </section>
        <section className="flex justify-center gap-[4px]">
          <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-[300] text-[#484848]">
            Equip:
          </span>
          <span className="mx-4 text-[12px] font-[400] text-[#484848]">
            {data.equipment_type?.description
              ? data.equipment_type?.description
              : "- -"}
          </span>
        </section>
        <section className="flex justify-center gap-[4px]">
          <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-[300] text-[#484848]">
            Weight:
          </span>
          <span className="ml-4 text-[12px] font-[400] text-[#484848]">
            {data.weight ? data.weight : "- -"} lb
          </span>
        </section>
      </div>

      <div className="grid grid-cols-2 items-center justify-between gap-4">
        <div className="flex items-center justify-center">
          <span
            className={["rounded px-2 py-1 text-xs font-bold text-black"].join(
              " "
            )}
            style={{
              backgroundColor: findTenderTypeColor(data.tender_type_id),
            }}
          >
            {data.tender_type_id ? data.tender_type_id : "- -"}
          </span>
        </div>
        <div className="flex items-center justify-center">
          <span
            className={["rounded px-2 py-1 text-xs font-bold text-black"].join(
              " "
            )}
            style={{
              backgroundColor: findOrderTypeColor(data.order_type_id || ""),
            }}
          >
            {data.order_type_id ? data.order_type_id : "- -"}
          </span>
        </div>
      </div>
    </div>
  );
};

const OfferCardComponent3 = ({
  data,
  cardType,
  showHighlighted = false,
  isManaged = false,
  isRemoved = false,
}: OfferCardProps): React.ReactElement => {
  const { data: user } = useGetUserInfo();

  const [view, setView] = useState<OfferViewT>(
    user?.default_lists_view ?? "offer"
  );
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (user?.default_lists_view) {
      setView(user?.default_lists_view);
    }
  }, [user]);

  useEffect(() => {
    // Function to update the showDetails state based on the screen width
    const updateShowDetails = (): void => {
      if (window.innerWidth >= 1024) {
        setShowDetails(false);
      }
    };

    // Call the function on the initial component mount
    updateShowDetails();

    // Add a listener for the window resize event
    window.addEventListener("resize", updateShowDetails);

    // Clean up the event listener when the component is unmounted
    return () => {
      window.removeEventListener("resize", updateShowDetails);
    };
  }, []);

  const toggleDetails = (): void => {
    setShowDetails((prev) => !prev);
  };

  return (
    <div id={`offer-card-${data.approval_id}`}>
      <CardFlip
        isFlipped={view === "rep" && !isManaged && !isRemoved}
        flipSpeedFrontToBack={800}
        flipSpeedBackToFront={800}
      >
        <OfferCardLayout
          showDetails={showDetails}
          showHighlighted={showHighlighted}
          isManaged={isManaged}
          view="offer"
          isRemoved={isRemoved}
          data={data}
          isDedicated={data.type === "dedicated"}
          toggleView={() => !isManaged && setView("rep")}
          showEditDeleteOptions={cardType === "queue"}
          showRenewOption={cardType === "history" && data.status === "rejected"}
          section1={<CarrierSection data={data} />}
          section2={<LocationsSection data={data} cardType={cardType} />}
          section3={<CustomerSection data={data} />}
          section4={
            <RatesSection
              data={data}
              view={view}
              setView={() => setView("rep")}
            />
          }
          details={
            <OfferCardDetails
              offer={data}
              showDetails={showDetails}
              toggleDetails={toggleDetails}
              view="offer"
              toggleView={() => !isManaged && setView("rep")}
            />
          }
        />

        {!isManaged && data.type !== "dedicated" ? (
          <SlideToggle
            duration={500}
            easeCollapse={eases["quintOut"]}
            easeExpand={eases["quintOut"]}
            render={({
              onToggle,
              setCollapsibleElement,
              toggleState,
            }: {
              onToggle: () => void;
              setCollapsibleElement: LegacyRef<HTMLDivElement>;
              toggleState: string;
            }) => (
              <OfferCardLayout
                showDetails={showDetails}
                showHighlighted={showHighlighted}
                data={data}
                view="rep"
                isDedicated={data.type === "dedicated"}
                tableViewIsOpen={
                  toggleState === "EXPANDED" || toggleState === "EXPANDING"
                }
                toggleTable={onToggle}
                toggleView={() => setView("offer")}
                section1={<CarrierSection data={data} />}
                section2={<LocationsRepViewSection data={data} />}
                section3={<div />}
                section4={
                  <RatesSection
                    data={data}
                    view={view}
                    setView={() => setView("offer")}
                  />
                }
                details={
                  <OfferCardDetails
                    offer={data}
                    showDetails={showDetails}
                    toggleDetails={toggleDetails}
                    view="rep"
                    tableViewIsOpen={
                      toggleState === "EXPANDED" || toggleState === "EXPANDING"
                    }
                    toggleTable={onToggle}
                    toggleView={() => setView("offer")}
                  />
                }
                table={
                  <section ref={setCollapsibleElement}>
                    <OfferCardRepViewTable
                      offer={data}
                      isOpen={view === "rep"}
                    />
                  </section>
                }
              />
            )}
          />
        ) : (
          <section />
        )}
      </CardFlip>
    </div>
  );
};

export const OfferCard2 = React.memo(
  OfferCardComponent3,
  (prevProps: OfferCardProps, nextProps: OfferCardProps) =>
    prevProps.data.approval_id === nextProps.data.approval_id &&
    prevProps.cardType === nextProps.cardType &&
    prevProps.showHighlighted === nextProps.showHighlighted &&
    prevProps.isManaged === nextProps.isManaged &&
    prevProps.isRemoved === nextProps.isRemoved &&
    isEqual(prevProps.data, nextProps.data)
);
